/**
 * MobileCartBar — Blinkit's signature sticky bottom "View Cart" pill.
 *
 * Renders only when:
 *   - viewport is < 768px (enforced via MobileCartBar.css media query)
 *   - user is an authenticated customer
 *   - cart has at least 1 item
 *   - we are NOT already on /cart, /checkout, or any /admin page
 *
 * Tapping it routes to the cart for the store with the most items
 * (or `/cart` if a single-store cart route exists).
 *
 * Styling lives in `MobileCartBar.css` — Tailwind is declared in package.json
 * but is not actually compiled in this CRA build, so we use plain CSS.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useCartSummary from '../hooks/useCartSummary';
import './MobileCartBar.css';

const HIDE_ON = ['/cart', '/checkout', '/admin', '/login', '/signup', '/onboarding'];

export const MobileCartBar: React.FC = () => {
  const { item_count, total, store_count, stores } = useCartSummary();
  const navigate = useNavigate();
  const location = useLocation();

  if (item_count <= 0) return null;
  if (HIDE_ON.some((p) => location.pathname.startsWith(p))) return null;

  const handleClick = () => {
    if (store_count === 1 && stores[0]?.store_id) {
      // /cart accepts ?store_id=N for single-store mode (see Cart.tsx).
      // The earlier /cart/:storeId path was never declared in App.tsx and
      // 404'd, which is one of the reasons the cart felt "broken".
      navigate(`/cart?store_id=${stores[0].store_id}`);
    } else {
      navigate('/cart');
    }
  };

  const pretty = `₹${total.toFixed(total % 1 === 0 ? 0 : 2)}`;
  const itemLabel = item_count === 1 ? 'item' : 'items';

  return (
    <AnimatePresence>
      <motion.button
        key="mobile-cart-bar"
        type="button"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        onClick={handleClick}
        className="mcb-root"
        aria-label={`View cart with ${item_count} ${itemLabel}, total ${pretty}`}
      >
        <span className="mcb-left">
          <span className="mcb-bag" aria-hidden>
            🛒
            <span className="mcb-pip">{item_count}</span>
          </span>
          <span>
            {item_count} {itemLabel}
            {store_count > 1 && (
              <span className="mcb-stores">· {store_count} stores</span>
            )}
            <span className="mcb-total">{pretty}</span>
          </span>
        </span>
        <span className="mcb-cta">
          View Cart <span aria-hidden>→</span>
        </span>
      </motion.button>
    </AnimatePresence>
  );
};

export default MobileCartBar;

